import { useEffect, useRef, useState } from 'react'

import { useAuthStore } from '@/modules/auth'

import { MeetingAssistCue } from '../model/meetingInterfaces'

const BASE_URL = import.meta.env.VITE_API_BASE_URL as string

const useMeetingAssist = (meetingId: string | null, enabled: boolean) => {
  const [cues, setCues] = useState<MeetingAssistCue[]>([])
  const cursorRef = useRef<string | null>(null)
  const authContext = useAuthStore((state) => state.authContext)
  const [reconnectToken, setReconnectToken] = useState(0)

  useEffect(() => {
    cursorRef.current = null
    setCues([])
  }, [meetingId])

  useEffect(() => {
    if (!enabled || !meetingId || !authContext) {
      return
    }
    if (typeof EventSource === 'undefined') {
      console.warn('EventSource is not available in this environment')
      return
    }
    let active = true
    const params = new URLSearchParams()
    params.set('zoomAppContext', authContext)
    if (cursorRef.current) {
      params.set('since', cursorRef.current)
    }
    const url = `${BASE_URL}/api/meetings/${meetingId}/assist/stream?${params.toString()}`
    const source = new EventSource(url)

    source.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as {
          type?: string
          id?: string
          createdAt?: string
        }
        if (!parsed || parsed.type === 'keepalive' || !parsed.id || !parsed.createdAt) {
          return
        }
        setCues((prev) => {
          const next = [...prev, parsed as MeetingAssistCue]
          if (next.length <= 200) {
            return next
          }
          return next.slice(-200)
        })
        cursorRef.current = parsed.createdAt
      } catch (error) {
        console.warn('Failed to parse assist cue', error)
      }
    }

    source.onerror = () => {
      if (!active) {
        return
      }
      source.close()
      setTimeout(() => {
        if (active) {
          setReconnectToken((token) => token + 1)
        }
      }, 2000)
    }

    return () => {
      active = false
      source.close()
    }
  }, [enabled, meetingId, authContext, reconnectToken])

  return cues
}

export default useMeetingAssist
